import React, { useState, useEffect, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAuth } from '../../contexts/AuthContext';
import './SettingsModal.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const SettingsModal = ({ isOpen, onClose, profile, trainingPlan, onProfileUpdate, onTrainingPlanUpdate }) => {
  const { authFetch } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageWasUploaded, setImageWasUploaded] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  // VDOT preview state
  const [previewVdot, setPreviewVdot] = useState(null);
  const [previewPacesKm, setPreviewPacesKm] = useState(null);
  const [previewPacesMile, setPreviewPacesMile] = useState(null);
  const [vdotLoading, setVdotLoading] = useState(false);
  const [vdotChanged, setVdotChanged] = useState(false);

  const [formData, setFormData] = useState({
    // Profile tab
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    unitSystem: 'metric',
    weight: '',
    height: '',
    heightFeet: '',
    heightInches: '',
    profileImage: null,
    profileImagePreview: null,

    // Training tab
    preferredUnits: 'km',
    temperatureUnits: 'celsius',
    recentRaceDistance: '5k',
    recentRaceTime: '',
    personalBests: {
      '1km': '',
      '1500m': '',
      '1mile': '',
      '5km': '',
      '10km': '',
      'half_marathon': '',
      'marathon': ''
    },
    trainingLevel: '',
    longRunDay: 'sunday',

    // Privacy tab
    messagePrivacy: 'followers_only',
    groupInvitePrivacy: 'followers_only'
  });

  // Fetch privacy settings
  useEffect(() => {
    const fetchPrivacySettings = async () => {
      if (!isOpen) return;
      try {
        const response = await authFetch('/api/messages/privacy');
        if (response.ok) {
          const data = await response.json();
          setFormData(prev => ({
            ...prev,
            messagePrivacy: data.message_privacy || 'followers_only',
            groupInvitePrivacy: data.group_invite_privacy || 'followers_only'
          }));
        }
      } catch (err) {
        console.error('Failed to fetch privacy settings:', err);
      }
    };
    fetchPrivacySettings();
  }, [isOpen, authFetch]);

  useEffect(() => {
    if (profile) {
      // Reset upload tracking when modal opens with new profile data
      setImageWasUploaded(false);

      // Parse name into first and last
      const fullName = profile.name || '';
      const nameParts = fullName.split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Handle profile image URL
      let profileImageUrl = profile.profileImage || profile.profile_image || null;
      if (profileImageUrl && !profileImageUrl.startsWith('http') && !profileImageUrl.startsWith('data:')) {
        profileImageUrl = `${API_BASE_URL}${profileImageUrl}`;
      }

      setFormData(prev => ({
        ...prev,
        firstName,
        lastName,
        dateOfBirth: profile.dateOfBirth || profile.date_of_birth || '',
        gender: profile.gender || '',
        unitSystem: profile.unitSystem || profile.unit_system || 'metric',
        weight: profile.weight || '',
        height: profile.height || '',
        heightFeet: profile.heightFeet || profile.height_feet || '',
        heightInches: profile.heightInches || profile.height_inches || '',
        profileImage: null,
        profileImagePreview: profileImageUrl,

        preferredUnits: profile.preferredUnits || profile.preferred_units || 'km',
        temperatureUnits: profile.temperatureUnits || profile.temperature_units || 'celsius',
        recentRaceDistance: profile.recentRaceDistance || profile.recent_race_distance || '5k',
        recentRaceTime: profile.recentRaceTime || profile.recent_race_time || '',
        personalBests: profile.personalBests || profile.personal_bests || {
          '1km': '',
          '1500m': '',
          '1mile': '',
          '5km': '',
          '10km': '',
          'half_marathon': '',
          'marathon': ''
        },
        trainingLevel: profile.trainingLevel || profile.training_level || profile.mileageLevel || profile.mileage_level || '',
        longRunDay: profile.longRunDay || profile.long_run_day || 'sunday'
      }));
    }
  }, [profile]);

  // Calculate VDOT when race time or distance changes
  useEffect(() => {
    const calculateVdot = async () => {
      if (!formData.recentRaceTime || !formData.recentRaceDistance) {
        setPreviewVdot(null);
        setPreviewPacesKm(null);
        setPreviewPacesMile(null);
        setVdotChanged(false);
        return;
      }

      // Map form distance values to API distance values
      const distanceMap = {
        '1500m': '1500m',
        'mile': 'mile',
        '5k': '5k',
        '10k': '10k',
        'half_marathon': 'half_marathon',
        'marathon': 'marathon',
        'ultra_marathon': null // Not supported by VDOT calculator
      };

      const apiDistance = distanceMap[formData.recentRaceDistance];
      if (!apiDistance) {
        setPreviewVdot(null);
        setPreviewPacesKm(null);
        setPreviewPacesMile(null);
        return;
      }

      // Validate time format (MM:SS or HH:MM:SS)
      const timeRegex = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;
      if (!timeRegex.test(formData.recentRaceTime)) {
        return;
      }

      setVdotLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/vdot/calculate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            distance: apiDistance,
            time: formData.recentRaceTime
          })
        });

        if (response.ok) {
          const data = await response.json();
          setPreviewVdot(data.vdot);
          setPreviewPacesKm(data.training_paces_km);
          setPreviewPacesMile(data.training_paces_mile);
          // Check if VDOT changed from the current training plan
          setVdotChanged(trainingPlan?.vdot !== data.vdot);
        }
      } catch (err) {
        console.error('Failed to calculate VDOT:', err);
      } finally {
        setVdotLoading(false);
      }
    };

    // Debounce the API call
    const timeoutId = setTimeout(calculateVdot, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.recentRaceTime, formData.recentRaceDistance, trainingPlan?.vdot]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
    setSuccess(false);
  };

  const handlePBChange = (distance, value) => {
    setFormData(prev => ({
      ...prev,
      personalBests: {
        ...prev.personalBests,
        [distance]: value
      }
    }));
    setError(null);
    setSuccess(false);
  };

  const handleCancel = () => {
    // If an image was uploaded during this session, update the parent state
    // so the header shows the new image (since it's already saved on the server)
    if (imageWasUploaded && formData.profileImagePreview) {
      onProfileUpdate({
        ...profile,
        profileImage: formData.profileImagePreview,
        profile_image: formData.profileImagePreview
      });
    }
    onClose();
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be less than 5MB');
        return;
      }

      setError(null);
      setImageUploading(true);

      try {
        // Create FormData for file upload
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        // Upload to backend
        const response = await authFetch('/api/user/profile/upload-image', {
          method: 'POST',
          body: formDataUpload
        });

        if (response.ok) {
          const data = await response.json();
          // Construct full URL for display
          const imageUrl = data.image_url.startsWith('http')
            ? data.image_url
            : `${API_BASE_URL}${data.image_url}`;

          setFormData(prev => ({
            ...prev,
            profileImage: null,
            profileImagePreview: imageUrl
          }));
          setImageWasUploaded(true);
          setSuccess(true);
          setTimeout(() => setSuccess(false), 2000);
        } else {
          const errData = await response.json();
          // Handle Pydantic validation errors (array) or simple string errors
          let errorMsg = 'Failed to upload image';
          if (typeof errData.detail === 'string') {
            errorMsg = errData.detail;
          } else if (Array.isArray(errData.detail) && errData.detail.length > 0) {
            errorMsg = errData.detail[0].msg || errData.detail[0].message || errorMsg;
          }
          setError(errorMsg);
        }
      } catch (err) {
        console.error('Image upload error:', err);
        setError('Failed to upload image. Please try again.');
      } finally {
        setImageUploading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Combine first and last name
      const fullName = `${formData.firstName} ${formData.lastName}`.trim();

      // Save profile data
      const response = await authFetch('/api/user/profile', {
        method: 'PUT',
        body: JSON.stringify({
          name: fullName,
          date_of_birth: formData.dateOfBirth,
          gender: formData.gender,
          unit_system: formData.unitSystem,
          weight: formData.weight,
          height: formData.height,
          height_feet: formData.heightFeet,
          height_inches: formData.heightInches,
          preferred_units: formData.preferredUnits,
          temperature_units: formData.temperatureUnits,
          recent_race_distance: formData.recentRaceDistance,
          recent_race_time: formData.recentRaceTime,
          personal_bests: formData.personalBests,
          training_level: formData.trainingLevel,
          long_run_day: formData.longRunDay
        })
      });

      // Save privacy settings separately
      await authFetch('/api/messages/privacy', {
        method: 'PUT',
        body: JSON.stringify({
          message_privacy: formData.messagePrivacy
        })
      });

      await authFetch('/api/messages/privacy/group-invites', {
        method: 'PUT',
        body: JSON.stringify({
          group_invite_privacy: formData.groupInvitePrivacy
        })
      });

      if (response.ok) {
        // Update the profile with all changes including the image
        onProfileUpdate({
          ...profile,
          name: fullName,
          firstName: formData.firstName,
          lastName: formData.lastName,
          dateOfBirth: formData.dateOfBirth,
          date_of_birth: formData.dateOfBirth,
          gender: formData.gender,
          unitSystem: formData.unitSystem,
          unit_system: formData.unitSystem,
          weight: formData.weight,
          height: formData.height,
          heightFeet: formData.heightFeet,
          height_feet: formData.heightFeet,
          heightInches: formData.heightInches,
          height_inches: formData.heightInches,
          profileImage: formData.profileImagePreview,
          profile_image: formData.profileImagePreview,
          preferredUnits: formData.preferredUnits,
          preferred_units: formData.preferredUnits,
          temperatureUnits: formData.temperatureUnits,
          temperature_units: formData.temperatureUnits,
          recentRaceDistance: formData.recentRaceDistance,
          recent_race_distance: formData.recentRaceDistance,
          recentRaceTime: formData.recentRaceTime,
          recent_race_time: formData.recentRaceTime,
          personalBests: formData.personalBests,
          personal_bests: formData.personalBests,
          trainingLevel: formData.trainingLevel,
          training_level: formData.trainingLevel,
          longRunDay: formData.longRunDay,
          long_run_day: formData.longRunDay
        });

        // Update training plan with new VDOT and paces if changed
        if (vdotChanged && previewVdot && onTrainingPlanUpdate && trainingPlan) {
          const updatedTrainingPlan = {
            ...trainingPlan,
            vdot: previewVdot,
            training_paces: previewPacesKm // Store paces in km as canonical format
          };
          onTrainingPlanUpdate(updatedTrainingPlan);
        }

        // Close the modal after successful save
        onClose();
      } else {
        const data = await response.json();
        // Handle Pydantic validation errors (array) or simple string errors
        let errorMsg = 'Failed to update profile';
        if (typeof data.detail === 'string') {
          errorMsg = data.detail;
        } else if (Array.isArray(data.detail) && data.detail.length > 0) {
          errorMsg = data.detail[0].msg || data.detail[0].message || errorMsg;
        }
        setError(errorMsg);
      }
    } catch (err) {
      setError('Failed to save changes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const raceDistances = [
    { value: '1500m', label: '1500m' },
    { value: '5k', label: '5K' },
    { value: '10k', label: '10K' },
    { value: 'half_marathon', label: 'Half Marathon' },
    { value: 'marathon', label: 'Marathon' },
    { value: 'ultra_marathon', label: 'Ultra Marathon' }
  ];

  const daysOfWeek = [
    { value: 'monday', label: 'Monday' },
    { value: 'tuesday', label: 'Tuesday' },
    { value: 'wednesday', label: 'Wednesday' },
    { value: 'thursday', label: 'Thursday' },
    { value: 'friday', label: 'Friday' },
    { value: 'saturday', label: 'Saturday' },
    { value: 'sunday', label: 'Sunday' }
  ];

  const trainingLevels = [
    { value: 'performance', label: 'Performance', desc: '4-5 days/week' },
    { value: 'high_performance', label: 'High Performance', desc: '5-6 days/week' },
    { value: 'elite', label: 'Elite', desc: '6-7 days/week' }
  ];

  const pbDistances = [
    { key: '1km', label: '1km' },
    { key: '1500m', label: '1500m' },
    { key: '1mile', label: '1 Mile' },
    { key: '5km', label: '5K' },
    { key: '10km', label: '10K' },
    { key: 'half_marathon', label: 'Half Marathon' },
    { key: 'marathon', label: 'Marathon' }
  ];

  return (
    <div className="settings-modal-overlay" onClick={handleCancel}>
      <div className="settings-modal" onClick={e => e.stopPropagation()}>
        <div className="settings-header">
          <h2>Settings</h2>
          <button className="close-btn" onClick={handleCancel}>&times;</button>
        </div>

        <div className="settings-tabs">
          <button
            className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
          <button
            className={`tab-btn ${activeTab === 'training' ? 'active' : ''}`}
            onClick={() => setActiveTab('training')}
          >
            Training
          </button>
          <button
            className={`tab-btn ${activeTab === 'privacy' ? 'active' : ''}`}
            onClick={() => setActiveTab('privacy')}
          >
            Privacy
          </button>
        </div>

        <form onSubmit={handleSubmit} className="settings-form">
          {activeTab === 'profile' && (
            <div className="tab-content">
              {/* Profile Image */}
              <div className="profile-image-section">
                <div
                  className={`profile-image-preview ${imageUploading ? 'uploading' : ''}`}
                  onClick={() => !imageUploading && fileInputRef.current?.click()}
                >
                  {imageUploading ? (
                    <div className="profile-image-placeholder">
                      <span className="upload-spinner">⏳</span>
                      <span className="placeholder-text">Uploading...</span>
                    </div>
                  ) : formData.profileImagePreview ? (
                    <img src={formData.profileImagePreview} alt="Profile" />
                  ) : (
                    <div className="profile-image-placeholder">
                      <span>📷</span>
                      <span className="placeholder-text">Add Photo</span>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className="change-photo-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={imageUploading}
                >
                  {imageUploading ? 'Uploading...' : 'Change Photo'}
                </button>
              </div>

              {/* Name Row */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="First name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Last name"
                  />
                </div>
              </div>

              {/* DOB and Gender Row */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dateOfBirth">Date of Birth *</label>
                  <DatePicker
                    id="dateOfBirth"
                    selected={formData.dateOfBirth ? new Date(formData.dateOfBirth + 'T00:00:00') : null}
                    onChange={(date) => {
                      if (!date) {
                        setFormData(prev => ({ ...prev, dateOfBirth: '' }));
                        setError(null);
                        setSuccess(false);
                        return;
                      }
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const dateStr = `${year}-${month}-${day}`;
                      setFormData(prev => ({ ...prev, dateOfBirth: dateStr }));
                      setError(null);
                      setSuccess(false);
                    }}
                    dateFormat="dd/MM/yyyy"
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    openToDate={formData.dateOfBirth ? new Date(formData.dateOfBirth + 'T00:00:00') : new Date(new Date().setFullYear(new Date().getFullYear() - 30))}
                    maxDate={new Date(new Date().setFullYear(new Date().getFullYear() - 13))}
                    minDate={new Date(new Date().setFullYear(new Date().getFullYear() - 100))}
                    placeholderText="Select date"
                    className="datepicker-input"
                    showIcon
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="gender">Gender *</label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              </div>

              {/* Unit System */}
              <div className="form-group">
                <label htmlFor="unitSystem">Unit Preferences *</label>
                <select
                  id="unitSystem"
                  name="unitSystem"
                  value={formData.unitSystem}
                  onChange={handleInputChange}
                >
                  <option value="metric">Metric (kg, cm)</option>
                  <option value="imperial">Imperial (lbs, ft/in)</option>
                </select>
              </div>

              {/* Preferred Temperature Units */}
              <div className="form-group">
                <label htmlFor="temperatureUnits">Preferred Temperature Units *</label>
                <select
                  id="temperatureUnits"
                  name="temperatureUnits"
                  value={formData.temperatureUnits}
                  onChange={handleInputChange}
                >
                  <option value="celsius">Celsius (°C)</option>
                  <option value="fahrenheit">Fahrenheit (°F)</option>
                </select>
                <small>Used for weather-related training recommendations</small>
              </div>

              {/* Weight and Height Row */}
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="weight">Weight {formData.unitSystem === 'metric' ? '(kg)' : '(lbs)'} *</label>
                  <input
                    type="number"
                    id="weight"
                    name="weight"
                    value={formData.weight}
                    onChange={handleInputChange}
                    placeholder={formData.unitSystem === 'metric' ? '70' : '154'}
                    min="20"
                    max={formData.unitSystem === 'metric' ? '300' : '660'}
                    step="0.1"
                  />
                </div>
                {formData.unitSystem === 'metric' ? (
                  <div className="form-group">
                    <label htmlFor="height">Height (cm) *</label>
                    <input
                      type="number"
                      id="height"
                      name="height"
                      value={formData.height}
                      onChange={handleInputChange}
                      placeholder="175"
                      min="100"
                      max="250"
                    />
                  </div>
                ) : (
                  <div className="form-group">
                    <label>Height *</label>
                    <div className="height-imperial">
                      <input
                        type="number"
                        id="heightFeet"
                        name="heightFeet"
                        value={formData.heightFeet}
                        onChange={handleInputChange}
                        placeholder="5"
                        min="3"
                        max="8"
                      />
                      <span className="height-unit">ft</span>
                      <input
                        type="number"
                        id="heightInches"
                        name="heightInches"
                        value={formData.heightInches}
                        onChange={handleInputChange}
                        placeholder="9"
                        min="0"
                        max="11"
                      />
                      <span className="height-unit">in</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'training' && (
            <div className="tab-content">
              {/* Training Paces Display */}
              {(previewVdot || trainingPlan?.vdot) && (
                <div className={`training-paces-display ${vdotChanged ? 'preview' : ''}`}>
                  <div className="vdot-display">
                    <span className="vdot-label">
                      Your Fitness Level (VDOT)
                      {vdotChanged && <span className="vdot-changed-badge">Updated</span>}
                    </span>
                    <span className="vdot-value">
                      {vdotLoading ? '...' : (previewVdot || trainingPlan?.vdot)}
                    </span>
                  </div>
                  {(() => {
                    // Determine which paces to show based on unit preference
                    const useMiles = formData.preferredUnits === 'miles';
                    const unitLabel = useMiles ? '/mi' : '/km';
                    const speedLabel = useMiles ? 'mph' : 'kph';

                    // Use preview paces if available, otherwise fall back to training plan
                    let paces;
                    if (previewVdot && (previewPacesKm || previewPacesMile)) {
                      paces = useMiles ? previewPacesMile : previewPacesKm;
                    } else if (trainingPlan?.training_paces) {
                      paces = trainingPlan.training_paces;
                    } else {
                      paces = null;
                    }

                    if (!paces) return null;

                    // Helper to extract pace value
                    const getPace = (paceData) => {
                      if (!paceData) return '-';
                      return typeof paceData === 'object' ? paceData.pace : paceData;
                    };

                    // Helper to extract speed value
                    const getSpeed = (paceData) => {
                      if (!paceData) return null;
                      if (typeof paceData === 'object') {
                        // For easy pace, use speed range
                        if (paceData.speed_min && paceData.speed_max) {
                          const minVal = paceData.speed_min.replace(/[^0-9.]/g, '');
                          const maxVal = paceData.speed_max.replace(/[^0-9.]/g, '');
                          return `${minVal}-${maxVal}`;
                        }
                        return paceData.speed ? paceData.speed.replace(/[^0-9.]/g, '') : null;
                      }
                      return null;
                    };

                    // Helper to get repetition pace per km/mile
                    const getRepPace = (paceData) => {
                      if (!paceData) return '-';
                      if (typeof paceData === 'object') {
                        const pace = useMiles ? paceData.pace_mile : paceData.pace_km;
                        // Fallback to pace_km or pace if mile not available, or vice versa
                        if (pace && pace !== '—') return pace;
                        if (paceData.pace_km && paceData.pace_km !== '—') return paceData.pace_km;
                        if (paceData.pace && paceData.pace !== '—') return paceData.pace;
                        return '-';
                      }
                      return '-';
                    };

                    return (
                      <div className="paces-grid">
                        <div className="pace-item easy">
                          <span className="pace-zone">Easy</span>
                          <span className="pace-value">{vdotLoading ? '...' : getPace(paces.easy)}</span>
                          <span className="pace-unit">{unitLabel}</span>
                          {getSpeed(paces.easy) && (
                            <span className="pace-speed">{getSpeed(paces.easy)} {speedLabel}</span>
                          )}
                        </div>
                        <div className="pace-item marathon">
                          <span className="pace-zone">Marathon</span>
                          <span className="pace-value">{vdotLoading ? '...' : getPace(paces.marathon)}</span>
                          <span className="pace-unit">{unitLabel}</span>
                          {getSpeed(paces.marathon) && (
                            <span className="pace-speed">{getSpeed(paces.marathon)} {speedLabel}</span>
                          )}
                        </div>
                        <div className="pace-item threshold">
                          <span className="pace-zone">Threshold</span>
                          <span className="pace-value">{vdotLoading ? '...' : getPace(paces.threshold)}</span>
                          <span className="pace-unit">{unitLabel}</span>
                          {getSpeed(paces.threshold) && (
                            <span className="pace-speed">{getSpeed(paces.threshold)} {speedLabel}</span>
                          )}
                        </div>
                        <div className="pace-item interval">
                          <span className="pace-zone">Interval</span>
                          <span className="pace-value">{vdotLoading ? '...' : getPace(paces.interval)}</span>
                          <span className="pace-unit">{unitLabel}</span>
                          {getSpeed(paces.interval) && (
                            <span className="pace-speed">{getSpeed(paces.interval)} {speedLabel}</span>
                          )}
                        </div>
                        <div className="pace-item repetition">
                          <span className="pace-zone">Repetition</span>
                          <span className="pace-value">{vdotLoading ? '...' : getRepPace(paces.repetition)}</span>
                          <span className="pace-unit">{unitLabel}</span>
                          {getSpeed(paces.repetition) && (
                            <span className="pace-speed">{getSpeed(paces.repetition)} {speedLabel}</span>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Preferred Distance Units */}
              <div className="form-group">
                <label htmlFor="preferredUnits">Preferred Distance Units *</label>
                <select
                  id="preferredUnits"
                  name="preferredUnits"
                  value={formData.preferredUnits}
                  onChange={handleInputChange}
                >
                  <option value="km">Kilometers (km)</option>
                  <option value="miles">Miles</option>
                </select>
                <small>Used for displaying distances in your training plan</small>
              </div>

              {/* Latest Race Time */}
              <div className="form-group">
                <label>Latest Race Time</label>
                <div className="race-time-row">
                  <select
                    id="recentRaceDistance"
                    name="recentRaceDistance"
                    value={formData.recentRaceDistance}
                    onChange={handleInputChange}
                  >
                    {raceDistances.map(race => (
                      <option key={race.value} value={race.value}>{race.label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    id="recentRaceTime"
                    name="recentRaceTime"
                    value={formData.recentRaceTime}
                    onChange={handleInputChange}
                    placeholder="e.g., 22:30"
                  />
                </div>
                <small>Format: MM:SS or HH:MM:SS</small>
              </div>

              {/* Training Level */}
              <div className="form-group">
                <label>Training Level *</label>
                <div className="training-level-options">
                  {trainingLevels.map(level => (
                    <label
                      key={level.value}
                      className={`training-level-option ${formData.trainingLevel === level.value ? 'selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name="trainingLevel"
                        value={level.value}
                        checked={formData.trainingLevel === level.value}
                        onChange={handleInputChange}
                      />
                      <div className="level-content">
                        <span className="level-name">{level.label}</span>
                        <span className="level-desc">{level.desc}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Long Run Day */}
              <div className="form-group">
                <label htmlFor="longRunDay">Preferred Long Run Day *</label>
                <select
                  id="longRunDay"
                  name="longRunDay"
                  value={formData.longRunDay}
                  onChange={handleInputChange}
                >
                  {daysOfWeek.map(day => (
                    <option key={day.value} value={day.value}>{day.label}</option>
                  ))}
                </select>
              </div>

              {/* Personal Bests */}
              <div className="form-group">
                <label>Personal Bests</label>
                <small className="pb-hint">Optional: Track your progress over time</small>
                <div className="pb-grid">
                  {pbDistances.map(({ key, label }) => (
                    <div key={key} className="pb-input-group">
                      <label htmlFor={`pb-${key}`}>{label}</label>
                      <input
                        type="text"
                        id={`pb-${key}`}
                        value={formData.personalBests[key] || ''}
                        onChange={(e) => handlePBChange(key, e.target.value)}
                        placeholder="MM:SS"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'privacy' && (
            <div className="tab-content">
              {/* Message Privacy */}
              <div className="form-group">
                <label htmlFor="messagePrivacy">Who Can Message You</label>
                <select
                  id="messagePrivacy"
                  name="messagePrivacy"
                  value={formData.messagePrivacy}
                  onChange={handleInputChange}
                >
                  <option value="followers_only">People You Follow</option>
                  <option value="everyone">Everyone</option>
                </select>
                <small>Control who can start a conversation with you</small>
              </div>

              {/* Group Invite Privacy */}
              <div className="form-group">
                <label htmlFor="groupInvitePrivacy">Who Can Add You to Groups</label>
                <select
                  id="groupInvitePrivacy"
                  name="groupInvitePrivacy"
                  value={formData.groupInvitePrivacy}
                  onChange={handleInputChange}
                >
                  <option value="followers_only">People You Follow</option>
                  <option value="everyone">Everyone</option>
                </select>
                <small>Control who can add you to clubs and groups</small>
              </div>

              <div className="privacy-info">
                <div className="privacy-info__icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                </div>
                <div className="privacy-info__text">
                  <strong>Privacy Note</strong>
                  <p>Regardless of this setting, you can always message anyone who follows you or whom you follow.</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="settings-error">{error}</div>
          )}

          {success && (
            <div className="settings-success">Changes saved successfully!</div>
          )}

          <div className="settings-footer">
            <button type="button" className="btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SettingsModal;
